import { inject } from '@angular/core';
import { ResolveFn, ActivatedRouteSnapshot, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TenantService } from '../services/tenant.service';
import { Tenant } from '../models/tenant.model';
import { TENANT_SHORT_NAME_TO_CODE } from '../config/tenant-mapping';

export const tenantConfigurationResolver: ResolveFn<Tenant | null> = async (
  route: ActivatedRouteSnapshot
) => {
  const router = inject(Router);
  const tenantService = inject(TenantService);

  try {
    let tenantShortName = route.paramMap.get('tenantShortName');
    if (!tenantShortName) {
      throw new Error('Tenant short name missing');
    }
    
    // Normalize: If URL parameter doesn't match exactly but there's a case-insensitive match,
    // use the correct case from the mapping to ensure strict matching downstream
    const normalizedInput = tenantShortName.toLowerCase();
    const matchingKey = Object.keys(TENANT_SHORT_NAME_TO_CODE).find(
      key => key.toLowerCase() === normalizedInput
    );
    
    if (!matchingKey) {
      // No matching tenant found in mapping, redirect to page not found
      router.navigate(['/page-not-found']);
      return null;
    }
    
    if (matchingKey !== tenantShortName) {
      tenantShortName = matchingKey; // Use the correct case from mapping
    }

    // Check if tenant was passed through navigation state (e.g., from logout)
    const navigation = router.getCurrentNavigation();
    const stateTenant = navigation?.extras?.state?.['tenant'] as Tenant | undefined;
    if (stateTenant && stateTenant.tenantShortName === tenantShortName) {
      // Validate tenant belongs to current environment
      if (!tenantService.isValidTenant(stateTenant.tenantCode)) {
        router.navigate(['/page-not-found']);
        return null;
      }
      tenantService.setCurrentTenant(stateTenant);
      return stateTenant;
    }

    // Check if current tenant already matches (avoid reloading)
    const currentTenant = tenantService.getCurrentTenant();
    if (currentTenant && currentTenant.tenantShortName === tenantShortName) {
      // Validate tenant belongs to current environment
      if (!tenantService.isValidTenant(currentTenant.tenantCode)) {
        router.navigate(['/page-not-found']);
        return null;
      }
      return currentTenant;
    }

    // Find tenant by short name (strict case-sensitive match)
    const tenant = await firstValueFrom(tenantService.getTenantByShortName(tenantShortName));
    
    if (tenant) {
      // Strict validation: URL parameter must exactly match tenant's short name from JSON file
      if (tenant.tenantShortName !== tenantShortName) {
        router.navigate(['/page-not-found']);
        return null;
      }
      
      // Validate tenant belongs to current environment
      if (!tenantService.isValidTenant(tenant.tenantCode)) {
        router.navigate(['/page-not-found']);
        return null;
      }
      tenantService.setCurrentTenant(tenant);
      return tenant;
    } else {
      throw new Error(`Failed to load tenant configuration for short name: ${tenantShortName}`);
    }
  } catch (error) {
    router.navigate(['/page-not-found']);
    return null;
  }
};